import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { BookOpen, Loader2 } from "lucide-react";

const CAT_COLOR = {
  Pressing: "#ff3b30", Defending: "#00e5ff", Possession: "#ccff00", Transitions: "#ff7300",
  Matchups: "#a78bfa", Attack: "#ff6ad5", Management: "#33d17a", "Set-pieces": "#ffb800", Philosophy: "#7dd3fc",
};

export default function CodexLibrary() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/codex").then((r) => setData(r.data)).catch(() => {}); }, []);

  const cats = data ? ["All", ...data.categories] : ["All"];
  const entries = data ? (cat === "All" ? data.entries : data.entries.filter((e) => e.category === cat)) : [];

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16">
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">STUDIED THEORY · THE LEARNED COACH</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Tactics Codex</h1>
      <p className="mt-2 max-w-2xl text-sm text-white/50">
        The football theory OCTON has studied and cites when it advises — pressing schemes, positional play,
        defensive blocks, transitions, matchups and coaching philosophies. {data ? `${data.count} principles absorbed.` : ""}
      </p>

      {!data ? (
        <div className="mt-8 flex items-center gap-2 text-white/50"><Loader2 className="animate-spin" size={18} /> Loading the codex…</div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="codex-filters" data-octon-section="codex filters search" data-octon-label="Codex Filters">
            {cats.map((c) => (
              <button key={c} data-testid={`codex-cat-${c}`} onClick={() => setCat(c)}
                className={`rounded-full border px-4 py-1.5 text-xs transition ${cat === c ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-white/50 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="codex-grid" data-octon-section="codex library knowledge documents" data-octon-label="Codex Library">
            {entries.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                className="card-tac flex flex-col p-5" data-testid={`codex-entry-${e.id}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-tac text-[8px] tracking-widest"
                    style={{ background: `${CAT_COLOR[e.category] || "#00e5ff"}1f`, color: CAT_COLOR[e.category] || "#00e5ff" }}>
                    <BookOpen size={10} /> {e.category.toUpperCase()}
                  </span>
                </div>
                <h3 className="font-head text-lg font-bold leading-tight tracking-tight text-white">{e.title}</h3>
                <p className="mt-2 flex-1 text-[13px] leading-relaxed text-white/65">{e.principle}</p>
                <p className="mt-3 text-[11px] text-white/40"><span className="text-white/60">When:</span> {e.when_to_use}</p>
                {e.coaches?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {e.coaches.map((c) => (
                      <span key={c} className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-white/50">{c}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
